import { MixerCard } from '@/components/MixerCard';
import { useTranslation } from '@/hooks/useTranslation';
import { LoadSurveyVotes, submitVote } from '@/services/feedbackApi';
import { loggingUtility } from '@/utils/loggingUtility';
import { getOrCreateSessionId } from '@/utils/sessionUtils';
import { Skeleton } from 'primereact/skeleton';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  startTransition,
} from 'react';
import './VotingInterface.css';

interface VotingInterfaceProps {
  surveyId: string;
  responseId?: string;
  userResponses?: Record<string, unknown>;
  showHeader?: boolean;
  mode?: 'standalone' | 'embedded'; // standalone for mixer page, embedded for survey completion
  onSurveyStatusLoaded?: (status: string) => void; // callback when survey status is loaded
}

interface Answer {
  id: string;
  questionId: string;
  answerValue: string;
  displayInitials?: string;
  createdAt: string;
  isFavorite: boolean;
  voteCount: number;
  isUserAnswer?: boolean;
}

const VotingInterface: React.FC<VotingInterfaceProps> = ({
  surveyId,
  responseId: _responseId = '',
  userResponses,
  showHeader = true,
  mode = 'standalone',
  onSurveyStatusLoaded,
}) => {
  const { t } = useTranslation();

  const sessionIdRef = useRef<string>(getOrCreateSessionId());

  // Height constant
  const ITEM_HEIGHT = 80; // Height of each MixerCard in pixels

  // Calculate max container height: min(viewport height, total items * item height)
  const getContainerMaxHeight = () => {
    const viewportHeight = window.innerHeight * 0.7; // Use 70% of viewport height
    const totalAnswers =
      typeof totalAnswersRef.current === 'number' &&
      !isNaN(totalAnswersRef.current)
        ? totalAnswersRef.current
        : 0;
    const totalContentHeight = totalAnswers * ITEM_HEIGHT;
    return Math.min(viewportHeight, totalContentHeight);
  };

  // Data loading state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allAnswers, setAllAnswers] = useState<Answer[]>([]); // Answers currently rendered
  const [questionText, setQuestionText] = useState<string | null>(null);
  const [isVotingEnabled, setIsVotingEnabled] = useState(false);
  const totalAnswersRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load voting data
  useEffect(() => {
    const loadVotingData = async () => {
      try {
        setLoading(true);
        setError(null);

        const votesResponse = await LoadSurveyVotes(
          surveyId,
          sessionIdRef.current
        );
        setQuestionText(votesResponse.mixerQuestionText || null);
        setIsVotingEnabled(votesResponse.isVotingEnabled);

        // Notify parent of survey status if callback provided
        if (onSurveyStatusLoaded && votesResponse.surveyStatus) {
          onSurveyStatusLoaded(votesResponse.surveyStatus);
        }

        // Convert existingVotes to flat array of answers
        const allAnswers: Answer[] = [];
        Object.entries(votesResponse.existingVotes || {}).forEach(
          ([questionId, votes]) => {
            votes.forEach(vote => {
              allAnswers.push({
                id: vote.id,
                questionId,
                answerValue: vote.answerText,
                displayInitials: vote.displayInitials,
                createdAt: vote.createdAt,
                isFavorite: vote.isFavorite || false,
                voteCount: vote.voteCount || 0,
                isUserAnswer:
                  userResponses &&
                  userResponses[questionId] === vote.answerText,
              });
            });
          }
        );

        // Sort answers: user's answer first, then others
        allAnswers.sort((a, b) => {
          if (a.isUserAnswer) return -1;
          if (b.isUserAnswer) return 1;
          return 0;
        });

        totalAnswersRef.current = allAnswers.length;

        // Render first 100 immediately for fast initial display (or all if less than 100)
        const INITIAL_BATCH = 100;
        const initialBatch = allAnswers.slice(0, INITIAL_BATCH);
        setAllAnswers(initialBatch);
        setLoading(false);

        // Load remaining answers in a low-priority transition (if there are more than 100)
        if (allAnswers.length > INITIAL_BATCH) {
          startTransition(() => {
            setAllAnswers(allAnswers);
          });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t('voting.errors.loadFailed');
        setError(errorMessage);
        loggingUtility.error('Failed to load voting data:', err);
        setLoading(false);
      }
    };

    loadVotingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t() is stable and doesn't need to be in deps
  }, [surveyId, userResponses]);

  const handleToggleFavorite = useCallback(
    async (answerId: string) => {
      // Find the answer to get its details
      const answer = allAnswers.find(a => a.id === answerId);

      if (!answer) {
        loggingUtility.error('Answer not found for voting:', answerId);
        return;
      }

      const updateAnswers = (prevAnswers: Answer[]) =>
        prevAnswers.map(a =>
          a.id === answerId
            ? {
                ...a,
                isFavorite: !a.isFavorite,
                voteCount: a.isFavorite
                  ? (a.voteCount || 0) - 1
                  : (a.voteCount || 0) + 1,
              }
            : a
        );

      // Store original state for potential revert
      const originalAllAnswers = [...allAnswers];

      try {
        setAllAnswers(prev => updateAnswers(prev));

        await submitVote(
          surveyId,
          answer.questionId,
          answerId,
          answer.answerValue,
          sessionIdRef.current
        );
      } catch (err) {
        loggingUtility.error('Failed to toggle vote:', err);
        setAllAnswers(originalAllAnswers);
      }
    },
    [allAnswers, surveyId]
  );

  const handleVote = (answerId: string) => {
    handleToggleFavorite(answerId);
  };

  // No lazy loading needed - all items loaded initially

  // Loading state
  if (loading) {
    return (
      <div className='max-w-4xl mx-auto p-6 space-y-4'>
        <Skeleton className='w-full h-15 mb-2' />
        <Skeleton className='w-full h-15 mb-2' />
        <Skeleton className='w-full h-15' />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='max-w-4xl mx-auto p-6'>
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-red-800'>{error}</p>
        </div>
      </div>
    );
  }

  // Render the voting content
  const votingContent = (
    <>
      {/* Community Mixer Section - Only show if showHeader is true */}
      {showHeader && (
        <div>
          <h1 className='text-2xl font-bold mb-6 text-blue-700'>
            {t('voting.communityMixer')}
          </h1>

          <p className='text-gray-700 text-base mb-6 leading-relaxed'>
            {t('voting.communityMixerDescription')}
          </p>
        </div>
      )}

      {/* Question text and response count */}
      <div className='mb-6'>
        {questionText && (
          <h2 className='text-lg font-bold mb-4 text-gray-900'>
            {questionText}
          </h2>
        )}
        {/* Total Responses Count - always show, even if 0 */}
        <div className='flex items-center gap-2 text-sm text-gray-600'>
          <i className='pi pi-comment text-base' aria-hidden='true' />
          <span>
            {totalAnswersRef.current} {t('voting.responses')}
          </span>
        </div>
      </div>

      {/* Empty state or scrollable container for answers */}
      {allAnswers.length === 0 ? (
        <div className='text-center py-12 px-6 bg-gray-50 rounded-lg border border-gray-200'>
          <i
            className='pi pi-inbox text-5xl text-gray-400 mb-4'
            aria-hidden='true'
          />
          <p className='text-gray-600 text-base'>{t('voting.noResponses')}</p>
        </div>
      ) : (
        <div
          ref={containerRef}
          role='region'
          aria-label={t('voting.scrollableAnswers')}
          className='scrollable-mixer-container hide-scrollbar'
          style={{ maxHeight: `${getContainerMaxHeight()}px` }}
        >
          {allAnswers.map(answer => (
            <MixerCard
              key={answer.id}
              answer={answer}
              displayName={answer.displayInitials}
              hasVoted={answer.isFavorite || false}
              voteCount={answer.voteCount || 0}
              isUserAnswer={answer.isUserAnswer || false}
              onVote={() => handleVote(answer.id)}
              isVotingEnabled={isVotingEnabled}
            />
          ))}
        </div>
      )}
    </>
  );

  // Embedded mode: return content without wrapper (parent will provide layout)
  if (mode === 'embedded') {
    return votingContent;
  }

  // Standalone mode: return content with wrapper
  return (
    <div className='space-y-6' data-testid='voting-results'>
      {votingContent}
    </div>
  );
};

export default VotingInterface;
